import { Palette, Gauge, Headphones, Gamepad2, Video } from "lucide-react";
import type { SessionSettings } from "./mock-data";

/**
 * SettingsSnapshot — a presentational grid of a SessionSettings object (ball color,
 * background, speed, audio, haptics, video). Used for BOTH a client's saved
 * preferences AND a session's settings snapshot, so the two never drift.
 *
 * HANDOFF NOTE: this is a shared presentational shape the real history UI should
 * reuse when it renders a session's settings snapshot (see /HISTORY_FIX_HANDOFF.md).
 */
export function SettingsSnapshot({ settings: s }: { settings: SessionSettings }) {
  return (
    <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
      <Swatch icon={<Palette className="h-3.5 w-3.5" />} label="Ball color" color={s.ballColor} />
      <Swatch icon={<Palette className="h-3.5 w-3.5" />} label="Background" color={s.canvasBg} />
      <Cell icon={<Gauge className="h-3.5 w-3.5" />} label="Speed" value={`${s.hz.toFixed(1)} Hz`} />
      <Cell icon={<Headphones className="h-3.5 w-3.5" />} label="Audio" value={s.audioOn ? `${cap(s.audioPreset)} · ${Math.round(s.audioVolume * 100)}%` : "Off"} on={s.audioOn} />
      <Cell icon={<Gamepad2 className="h-3.5 w-3.5" />} label="Haptics" value={s.hapticsOn ? `${Math.round(s.hapticsIntensity * 100)}%` : "Off"} on={s.hapticsOn} />
      <Cell icon={<Video className="h-3.5 w-3.5" />} label="Video" value={s.videoOn ? "On" : "Off"} on={s.videoOn} />
    </div>
  );
}

function Swatch({ icon, label, color }: { icon: React.ReactNode; label: string; color: string }) {
  return (
    <div className="flex items-center gap-2.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-deep)] px-3 py-2.5">
      <span className="h-7 w-7 shrink-0 rounded-full border border-[var(--color-border-strong)]" style={{ backgroundColor: color }} />
      <span className="min-w-0">
        <span className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.14em] text-[var(--color-text-ghost)]">{icon} {label}</span>
        <span className="block truncate font-mono text-[11px] text-[var(--color-text-muted)]">{color}</span>
      </span>
    </div>
  );
}

function Cell({ icon, label, value, on }: { icon: React.ReactNode; label: string; value: string; on?: boolean }) {
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-deep)] px-3 py-2.5">
      <span className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.14em] text-[var(--color-text-ghost)]">{icon} {label}</span>
      <span className={on === false ? "mt-0.5 block font-heading text-[15px] text-[var(--color-text-ghost)]" : "mt-0.5 block font-heading text-[15px] text-[var(--color-gold-soft)]"}>{value}</span>
    </div>
  );
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
